import { useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import { Search, Plus, Pencil, Trash2, ChevronLeft, ChevronRight, Copy, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Text, Badge, EmptyState, Modal } from '@/components/design-system'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import FleetMachineFormFields, { blankFleetMachineFormData, FleetMachineFormData } from './FleetMachineFormFields'
import {
  FleetMachineRow,
  FleetMachineTokenFlash,
  FLEET_MACHINE_PROVIDER_LABELS,
  FLEET_MACHINE_ENVIRONMENT_LABELS,
  FLEET_MACHINE_CONTROL_MODE_LABELS,
} from '@/frontend/types/fleetMachine'

interface Pagination { page: number; totalPages: number; total: number }

interface Props {
  fleet_machines: FleetMachineRow[]
  pagination: Pagination
  search: string
  flash_telemetry_token?: FleetMachineTokenFlash | null
  flash_api_token?: FleetMachineTokenFlash | null
}

function rowToFormData(row: FleetMachineRow): FleetMachineFormData {
  return {
    name: row.name,
    slug: row.slug,
    provider: row.provider,
    environment: row.environment,
    control_mode: row.control_mode,
    api_base_url: row.api_base_url ?? '',
    instance_url_template: row.instance_url_template ?? '',
    enabled: row.enabled ? 'true' : 'false',
    metadata: row.metadata && Object.keys(row.metadata).length > 0 ? JSON.stringify(row.metadata, null, 2) : '',
    generate_telemetry_token: 'false',
    generate_api_token: 'false',
  }
}

type ParseResult =
  | { ok: true; value: Record<string, unknown> }
  | { ok: false; error: string }

function parseMetadata(raw: string): ParseResult {
  const trimmed = raw.trim()
  if (!trimmed) return { ok: true, value: {} }
  try {
    const value = JSON.parse(trimmed)
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return { ok: false, error: 'Metadata precisa ser um objeto JSON.' }
    }
    return { ok: true, value: value as Record<string, unknown> }
  } catch {
    return { ok: false, error: 'JSON inválido em metadata.' }
  }
}

export default function Index({ fleet_machines, pagination, search, flash_telemetry_token, flash_api_token }: Props) {
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selected, setSelected] = useState<FleetMachineRow | null>(null)
  const [query, setQuery] = useState(search)
  const [metadataError, setMetadataError] = useState<string | undefined>(undefined)
  const [copiedToken, setCopiedToken] = useState<'telemetry' | 'api' | null>(null)

  const createForm = useForm(blankFleetMachineFormData())
  const editForm = useForm(blankFleetMachineFormData())

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    router.get('/cc/fleet_machines', { search: query }, { preserveState: true })
  }

  const goToPage = (page: number) => router.get('/cc/fleet_machines', { search, page }, { preserveState: true })

  const closeCreateModal = () => {
    setCreateModalOpen(false)
    createForm.reset()
    setMetadataError(undefined)
  }

  const closeEditModal = () => {
    setEditModalOpen(false)
    setSelected(null)
    editForm.reset()
    setMetadataError(undefined)
  }

  const buildSubmitPayload = (data: FleetMachineFormData, mode: 'create' | 'edit') => {
    const parsed = parseMetadata(data.metadata)
    if (!parsed.ok) {
      setMetadataError(parsed.error)
      return null
    }
    setMetadataError(undefined)
    const payload: Record<string, unknown> = {
      name: data.name,
      slug: data.slug,
      provider: data.provider,
      environment: data.environment,
      control_mode: data.control_mode,
      api_base_url: data.api_base_url,
      instance_url_template: data.instance_url_template,
      enabled: data.enabled === 'true',
      metadata: parsed.value,
    }
    if (mode === 'create' && data.generate_telemetry_token === 'true') {
      payload.generate_telemetry_token = true
    }
    if (mode === 'create' && data.generate_api_token === 'true') {
      payload.generate_api_token = true
    }
    return payload
  }

  const handleCreate = () => {
    const payload = buildSubmitPayload(createForm.data, 'create')
    if (!payload) return
    createForm.transform(() => payload)
    createForm.post('/cc/fleet_machines', { onSuccess: closeCreateModal })
  }

  const handleEdit = (machine: FleetMachineRow) => {
    setSelected(machine)
    editForm.setData(rowToFormData(machine))
    setEditModalOpen(true)
  }

  const handleUpdate = () => {
    if (!selected) return
    const payload = buildSubmitPayload(editForm.data, 'edit')
    if (!payload) return
    editForm.transform(() => payload)
    editForm.patch(`/cc/fleet_machines/${selected.id}`, { onSuccess: closeEditModal })
  }

  const handleDeleteClick = (machine: FleetMachineRow) => {
    setSelected(machine)
    setDeleteModalOpen(true)
  }

  const handleDelete = () => {
    if (!selected) return
    router.delete(`/cc/fleet_machines/${selected.id}`, {
      onSuccess: () => { setDeleteModalOpen(false); setSelected(null) },
    })
  }

  const handleRotateTelemetryToken = () => {
    if (!selected) return
    if (!confirm('Rotacionar o token de telemetria substitui o atual. Continuar?')) return
    router.post(`/cc/fleet_machines/${selected.id}/rotate_telemetry_token`, {}, {
      onSuccess: () => { setEditModalOpen(false); setSelected(null) },
    })
  }

  const handleRotateApiToken = () => {
    if (!selected) return
    if (!confirm('Rotacionar o token da API substitui o atual. Continuar?')) return
    router.post(`/cc/fleet_machines/${selected.id}/rotate_api_token`, {}, {
      onSuccess: () => { setEditModalOpen(false); setSelected(null) },
    })
  }

  // NOTE: extract <TokenRotationFlash> when a third token type appears.
  const copyToken = async (kind: 'telemetry' | 'api', token: string) => {
    try {
      await navigator.clipboard.writeText(token)
      setCopiedToken(kind)
      setTimeout(() => setCopiedToken(null), 2500)
    } catch {
      // clipboard unavailable — the token is already visible, user can select manually
    }
  }

  const hasNoMachinesAtAll = fleet_machines.length === 0 && !search
  const hasNoSearchResults = fleet_machines.length === 0 && !!search
  const hasBlockingReferences = (selected?.assigned_orgs_count ?? 0) > 0 || (selected?.assigned_workers_count ?? 0) > 0

  return (
    <div>
      {flash_telemetry_token && (
        <div className="mb-4 rounded-md border border-amber-500 bg-amber-50 dark:bg-amber-950 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <Text as="p">
                <strong>Token de telemetria para {flash_telemetry_token.machine_name}:</strong>
              </Text>
              <div className="mt-1 font-mono text-sm break-all select-all">{flash_telemetry_token.token}</div>
              <Text as="p" color="muted" size="small">
                Copie agora. Ele não será exibido novamente.
              </Text>
            </div>
            <Button variant="outline" size="sm" onClick={() => copyToken('telemetry', flash_telemetry_token.token)} aria-label="Copiar token">
              {copiedToken === 'telemetry' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}

      {flash_api_token && (
        <div className="mb-4 rounded-md border border-amber-500 bg-amber-50 dark:bg-amber-950 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <Text as="p">
                <strong>Token da API para {flash_api_token.machine_name}:</strong>
              </Text>
              <div className="mt-1 font-mono text-sm break-all select-all">{flash_api_token.token}</div>
              <Text as="p" color="muted" size="small">
                Copie agora. Ele não será exibido novamente. Aplique no host com <code className="font-mono">ocf api set-token</code>.
              </Text>
            </div>
            <Button variant="outline" size="sm" onClick={() => copyToken('api', flash_api_token.token)} aria-label="Copiar token">
              {copiedToken === 'api' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-foreground">Máquinas</h2>
          <span className="text-sm text-muted-foreground">({pagination.total})</span>
        </div>
        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nome ou slug..."
                aria-label="Buscar máquinas"
                className="pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring w-72"
              />
            </div>
            <Button type="submit" variant="outline" size="sm">Buscar</Button>
          </form>
          <Button size="sm" onClick={() => setCreateModalOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />Nova Máquina
          </Button>
        </div>
      </div>

      {hasNoMachinesAtAll ? (
        <EmptyState
          title="Nenhuma máquina cadastrada"
          description="Cadastre uma máquina para permitir atribuições a organizações e agentes."
          action={{ label: 'Nova Máquina', onClick: () => setCreateModalOpen(true) }}
        />
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-auto max-h-[calc(100vh-220px)]">
            <table className="w-full">
              <thead className="sticky top-0 z-10">
                <tr className="bg-muted border-b border-border">
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Nome</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Slug</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Provedor</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Ambiente</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Modo</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Orgs</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Agentes</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {fleet_machines.map((machine) => (
                  <tr key={machine.id} className="hover:bg-accent transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{machine.name}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground font-mono">{machine.slug}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{FLEET_MACHINE_PROVIDER_LABELS[machine.provider]}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{FLEET_MACHINE_ENVIRONMENT_LABELS[machine.environment]}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{FLEET_MACHINE_CONTROL_MODE_LABELS[machine.control_mode]}</td>
                    <td className="px-4 py-3"><Badge tone={machine.assigned_orgs_count > 0 ? 'info' : undefined}>{machine.assigned_orgs_count}</Badge></td>
                    <td className="px-4 py-3"><Badge tone={machine.assigned_workers_count > 0 ? 'info' : undefined}>{machine.assigned_workers_count}</Badge></td>
                    <td className="px-4 py-3">
                      <Badge tone={machine.enabled ? 'success' : 'warning'}>
                        {machine.enabled ? 'Habilitada' : 'Desabilitada'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(machine)} aria-label={`Editar ${machine.name}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(machine)} aria-label={`Excluir ${machine.name}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {hasNoSearchResults && (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-sm text-muted-foreground">Nenhuma máquina encontrada.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted">
              <span className="text-sm text-muted-foreground">Página {pagination.page} de {pagination.totalPages}</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => goToPage(pagination.page - 1)} aria-label="Página anterior">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => goToPage(pagination.page + 1)} aria-label="Próxima página">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <Modal open={createModalOpen} onClose={closeCreateModal} title="Nova Máquina" size="large"
        primaryAction={{ content: 'Criar', onAction: handleCreate, loading: createForm.processing }}
        secondaryAction={{ content: 'Cancelar', onAction: closeCreateModal }}>
        <FleetMachineFormFields
          data={createForm.data}
          errors={{ ...createForm.errors, metadata: metadataError ?? createForm.errors.metadata }}
          setData={(key, value) => createForm.setData(key, value)}
          mode="create"
          hasTelemetryToken={false}
          hasApiToken={false}
        />
      </Modal>

      <Modal open={editModalOpen} onClose={closeEditModal} title="Editar Máquina" size="large"
        primaryAction={{ content: 'Salvar', onAction: handleUpdate, loading: editForm.processing }}
        secondaryAction={{ content: 'Cancelar', onAction: closeEditModal }}>
        <FleetMachineFormFields
          data={editForm.data}
          errors={{ ...editForm.errors, metadata: metadataError ?? editForm.errors.metadata }}
          setData={(key, value) => editForm.setData(key, value)}
          mode="edit"
          hasTelemetryToken={selected?.telemetry_token_present ?? false}
          onRotateTelemetryToken={handleRotateTelemetryToken}
          hasApiToken={selected?.api_token_present ?? false}
          onRotateApiToken={handleRotateApiToken}
        />
      </Modal>

      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title="Excluir Máquina"
        destructiveAction={{ content: 'Excluir', onAction: handleDelete }}
        secondaryAction={{ content: 'Cancelar', onAction: () => setDeleteModalOpen(false) }}>
        <Text>Tem certeza que deseja excluir a máquina <strong>{selected?.name}</strong>?</Text>
        {hasBlockingReferences && (
          <p className="mt-2 text-sm text-amber-600">
            Esta máquina está atribuída a {selected?.assigned_orgs_count ?? 0} organização(ões) e {selected?.assigned_workers_count ?? 0} agente(s).
            Essas referências serão desassociadas ao excluí-la.
          </p>
        )}
      </Modal>
    </div>
  )
}

Index.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>
